import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EmailIcon from "@mui/icons-material/Email";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import SearchIcon from "@mui/icons-material/Search";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import VerifiedIcon from "@mui/icons-material/Verified";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import {
  Box,
  Button,
  Container,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { toast } from "react-toastify";
import Seo from "../components/Seo";

const brandBlue = "#1a237e";
const brandRed = "#d50000";

const steps = [
  {
    title: "Submit Requirements",
    desc: "Send your project needs.",
    icon: <AssignmentTurnedInIcon sx={{ fontSize: 40, color: "#1a237e" }} />,
  },
  {
    title: "We Analyze & Respond",
    desc: "Our team reviews details.",
    icon: <SearchIcon sx={{ fontSize: 40, color: "#1a237e" }} />,
  },
  {
    title: "Get Your Quote",
    desc: "Receive pricing & timeline.",
    icon: <MonetizationOnIcon sx={{ fontSize: 40, color: "#1a237e" }} />,
  },
];

export default function GetQuotePage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
    budget: "",
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); // prevent default form submit

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_key: "de439868-536f-48aa-8379-c6d5b7602433",
          ...formData,
        }),
      });

      const data = await response.json();

      if (data.success) {
        toast.success("Form submitted successfully!");
        setFormData({
          name: "",
          email: "",
          phone: "",
          company: "",
          message: "",
          budget: "",
        });
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch (error) {
      toast.error("Failed to submit form. Check your connection.");
    }
  };

  const fields = [
    {
      label: "Full Name *",
      name: "name",
      icon: <SupportAgentIcon />,
      required: true,
    },
    {
      label: "Email Address *",
      name: "email",
      icon: <EmailIcon />,
      required: true,
    },
    {
      label: "Phone Number *",
      name: "phone",
      icon: <PhoneIcon />,
      required: true,
    },
    { label: "Company Name", name: "company", icon: <CheckCircleIcon /> },
  ];

  return (
    <Box sx={{ bgcolor: "#f5f7fb" }}>
      <Seo
        title="Get a Quote | Burj Al Yarmouk Industries LLC"
        description="Request a quick and accurate quote for fabrication services, storage tanks, fuel tanks, generator enclosures, skid frames, steel structures, and custom engineering solutions. Burj Al Yarmouk Industries LLC provides fast response and competitive pricing."
        url="https://burjalyarmouk.com/get-quote"
        image="https://burjalyarmouk.com/og-banner.jpg"
        keywords="get quote Burj Al Yarmouk, fabrication quote UAE, tank fabrication quote, generator enclosure pricing, steel structure quote, custom fabrication estimate, engineering service quote"
        schema={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: "Get a Quote - Burj Al Yarmouk Industries LLC",
          url: "https://burjalyarmouk.com/get-quote",
          description:
            "Submit your project details to receive a custom quote for engineering and fabrication services from Burj Al Yarmouk Industries LLC.",
          publisher: {
            "@type": "Organization",
            name: "Burj Al Yarmouk Industries LLC",
            logo: "https://burjalyarmouk.com/favicon.ico",
            sameAs: [
              "https://www.instagram.com/burjalyarmouk/",
              "https://www.linkedin.com/in/burj-al-yarmouk-industries-llc-417570347/",
            ],
          },
          mainEntity: {
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "How can I get a quote for fabrication services?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Fill out the form on this page with your project details and our team will respond with a custom quote.",
                },
              },
              {
                "@type": "Question",
                name: "How long does it take to receive a quotation?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "We typically respond within 24–48 hours depending on project complexity.",
                },
              },
              {
                "@type": "Question",
                name: "What information is required for a fabrication quote?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Provide drawings, dimensions, material requirements, quantity, and delivery location for the most accurate quotation.",
                },
              },
            ],
          },
        }}
      />

      {/* =========================================
          HERO SECTION
      ========================================== */}
      <Box
        sx={{
          height: { xs: 300, md: 420 },
          position: "relative",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
          color: "#fff",
        }}
      >
        <img
          src="https://img.freepik.com/free-photo/engineer-working-night_23-2147965674.jpg"
          alt="Get Quote Hero"
          style={{
            position: "absolute",
            width: "100%",
            height: "100%",
            objectFit: "cover",
            top: 0,
            left: 0,
            zIndex: 1,
          }}
        />

        <Box
          sx={{
            position: "absolute",
            width: "100%",
            height: "100%",
            background: `${brandBlue}DD`,
            zIndex: 2,
          }}
        />

        <Container sx={{ zIndex: 3 }}>
          <Typography variant="h3" sx={{ fontWeight: 800, mb: 2 }}>
            Get Quote
          </Typography>
          <Box
            sx={{
              width: 80,
              height: 4,
              bgcolor: brandRed,
              mx: "auto",
              mb: 2,
              borderRadius: 2,
            }}
          />
          <Typography sx={{ maxWidth: 700, mx: "auto", fontSize: "1.2rem" }}>
            Provide your project details — our expert team responds within 24
            hours.
          </Typography>
        </Container>
      </Box>

      {/* =========================================
          HOW IT WORKS - 3 STEPS
      ========================================== */}
      <Container sx={{ py: 6 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 3,
            textAlign: "center",
          }}
        >
          {steps.map((step, i) => (
            <Box
              key={i}
              sx={{
                flex: "1 1 200px",
                background: "#fff",
                p: 3,
                borderRadius: 3,
                boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
              }}
            >
              <Box sx={{ mb: 2 }}>{step.icon}</Box>

              <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", mb: 1 }}>
                {step.title}
              </Typography>

              <Typography sx={{ opacity: 0.7 }}>{step.desc}</Typography>
            </Box>
          ))}
        </Box>
      </Container>

      {/* =========================================
          MAIN LAYOUT (FORM + SIDEBAR)
      ========================================== */}
      <Container>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "column" },
            gap: 4,
          }}
        >
          {/* ===================== FORM CONTAINER (GLASS + ICON INPUTS + LEFT IMAGE) ===================== */}
          <Box
            sx={{
              flexGrow: 1,
              p: 0,
              mt: 2,
              borderRadius: 4,
              boxShadow: "0 10px 40px rgba(0,0,0,0.08)",
              overflow: "hidden",
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              background: "rgba(255,255,255,0.65)",
              backdropFilter: "blur(12px)",
              border: "1px solid rgba(255,255,255,0.4)",
            }}
          >
            {/* -------- LEFT SIDE IMAGE -------- */}
            {/* <Box
              sx={{
                flex: "1 1 40%",
                minHeight: 500,
                backgroundImage:
                  "url('https://img.freepik.com/free-photo/group-engineers-working-night_23-2147965723.jpg')",
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            /> */}

            {/* -------- RIGHT SIDE FORM -------- */}
            <Box sx={{ flex: "1 1 60%", p: 5 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  textAlign: "center",
                  color: brandBlue,
                  fontSize: { xs: "1.8rem", md: "2.2rem" },
                  mb: 4,
                }}
              >
                Request a Quote
              </Typography>

              <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  flexDirection: { xs: "column", md: "row" },
                  gap: 3,
                }}
              >
                {fields.map((field, i) => (
                  <TextField
                    key={i}
                    label={field.label}
                    name={field.name}
                    value={formData[field.name]}
                    onChange={handleChange}
                    required={field.required}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ mr: 1, opacity: 0.7 }}>{field.icon}</Box>
                      ),
                    }}
                    sx={{
                      flex: "1 1 calc(50% - 16px)",
                      "& .MuiOutlinedInput-root": { borderRadius: 2 },
                    }}
                  />
                ))}

                <TextField
                  label="Project Requirements *"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  multiline
                  minRows={4}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <Box sx={{ mr: 1, opacity: 0.6, mt: 1.5 }}>
                        <SupportAgentIcon />
                      </Box>
                    ),
                  }}
                  sx={{
                    flex: "1 1 100%",
                    "& .MuiOutlinedInput-root": { borderRadius: 2 },
                  }}
                />

                <TextField
                  label="Estimated Budget (Optional)"
                  name="budget"
                  value={formData.budget}
                  onChange={handleChange}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <Box sx={{ mr: 1, opacity: 0.6 }}>
                        <CheckCircleIcon />
                      </Box>
                    ),
                  }}
                  sx={{
                    flex: "1 1 calc(50% - 16px)",
                    "& .MuiOutlinedInput-root": { borderRadius: 2 },
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  sx={{
                    mt: 2,
                    py: 1.7,
                    fontSize: "1.1rem",
                    fontWeight: 700,
                    background: brandRed,
                    color: "#fff",
                    borderRadius: 2,
                    boxShadow: "0 6px 15px rgba(0,0,0,0.15)",
                    "&:hover": { background: "#b00000" },
                  }}
                >
                  Submit Request
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>

      {/* ================= TRUST BADGES (UPDATED) ================= */}
      <Container sx={{ py: 10 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 5,
            flexWrap: "wrap",
          }}
        >
          {[
            { label: "Fast Response", icon: <CheckCircleIcon /> },
            { label: "Certified Experts", icon: <CheckCircleIcon /> },
            { label: "Guaranteed Quality", icon: <CheckCircleIcon /> },
            { label: "Secure Communication", icon: <CheckCircleIcon /> },
          ].map((item, i) => (
            <Box
              key={i}
              sx={{
                width: 200,
                background: "#fff",
                p: 3,
                textAlign: "center",
                borderRadius: 4,
                border: "1px solid #e0e3ec",
                boxShadow: "0 5px 18px rgba(0,0,0,0.08)",
                transition: "0.3s",
                "&:hover": {
                  transform: "translateY(-6px)",
                  boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
                },
              }}
            >
              <Box sx={{ color: brandBlue, fontSize: 45, mb: 1 }}>
                {item.icon}
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: "1.1rem" }}>
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Container>

      {/* =========================================
          CTA BANNER
      ========================================== */}

      <Box sx={{ mt: 10 }}>
        {/* ------------------- CURVED TOP BACKGROUND ------------------- */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #1a237e, #0d1446)",
            borderTopLeftRadius: "80px",
            borderTopRightRadius: "80px",
            pt: 10,
            pb: 8,
            textAlign: "center",
            color: "#fff",
          }}
        >
          <Container>
            {/* ------------------- BADGES ------------------- */}
            <Stack
              direction="row"
              spacing={4}
              justifyContent="center"
              flexWrap="wrap"
              sx={{ mb: 4 }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: "#fff",
                }}
              >
                <FlashOnIcon sx={{ color: "#d50000" }} />
                <Typography sx={{ fontWeight: 600 }}>Fast Service</Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: "#fff",
                }}
              >
                <VerifiedIcon sx={{ color: "#4cafef" }} />
                <Typography sx={{ fontWeight: 600 }}>
                  Certified Experts
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: "#fff",
                }}
              >
                <AccessTimeIcon sx={{ color: "#d50000" }} />
                <Typography sx={{ fontWeight: 600 }}>24/7 Support</Typography>
              </Box>
            </Stack>

            {/* ------------------- TITLE ------------------- */}
            <Typography
              variant="h4"
              fontWeight={800}
              sx={{
                mb: 2,
                fontSize: { xs: "2rem", md: "2.6rem" },
                color: "#fff",
              }}
            >
              Need Immediate Support?
            </Typography>

            {/* ------------------- SUBTEXT ------------------- */}
            <Typography
              sx={{
                mb: 4,
                color: "rgba(255,255,255,0.8)",
                maxWidth: 720,
                mx: "auto",
                fontSize: { xs: "0.98rem", md: "1.1rem" },
                lineHeight: 1.6,
              }}
            >
              Our team is available 24/7 to assist you with your project needs.
            </Typography>

            {/* ------------------- BUTTON ------------------- */}
            <Button
              href="tel:+971508517296"
              sx={{
                px: 4,
                py: 1.4,
                fontSize: "1.1rem",
                borderRadius: 2,
                background: "#d50000",
                color: "#fff",
                "&:hover": { background: "#b30000", color: "#fff" },
                boxShadow: "0 6px 18px rgba(0,0,0,0.2)",
                width: { xs: "100%", sm: "auto" },
              }}
            >
              Call Now
            </Button>
          </Container>
        </Box>
      </Box>

      {/* =========================================
    FLOATING SUPPORT WIDGET (UPDATED)
========================================== */}
      {/* <Box
        sx={{
          position: "fixed",
          right: 25,
          bottom: 90, // ⬆ moved up from 25 → 90
          zIndex: 999,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <IconButton
          size="large"
          sx={{
            bgcolor: brandBlue,
            color: "#fff",
            p: 1.8,
            boxShadow: "0 6px 15px rgba(0,0,0,0.2)",
            "&:hover": { bgcolor: "#000" },
          }}
          href="tel:+919876543210"
        >
          <PhoneIcon fontSize="medium" />
        </IconButton>

        <IconButton
          size="large"
          sx={{
            bgcolor: "green",
            color: "#fff",
            p: 1.8,
            boxShadow: "0 6px 15px rgba(0,0,0,0.2)",
            "&:hover": { bgcolor: "#0b7a0b" },
          }}
          href="https://wa.me/919876543210"
        >
          <WhatsAppIcon fontSize="medium" />
        </IconButton>

        <IconButton
          size="large"
          sx={{
            bgcolor: brandRed,
            color: "#fff",
            p: 1.8,
            boxShadow: "0 6px 15px rgba(0,0,0,0.2)",
            "&:hover": { bgcolor: "#a00000" },
          }}
          href="mailto:support@burjai.com"
        >
          <SupportAgentIcon fontSize="medium" />
        </IconButton>
      </Box> */}
    </Box>
  );
}
